import React from 'react';
import * as Yup from 'yup';

import { currencyEditor, dateEditor, textEditor } from '~components/table-view/columnEditors';
import { Column, EditorRenderer, Row } from '~components/table-view';
import SahBudgetActions from '~support-at-home/components/config/budgets/SahBudgetActions';

// Only value written by the Services Australia reference sync (api/src/libs/tav/client_sah_budgets.mjs
// mapApiBudgetToRow). A budget carrying this tag is overwritten by the next refresh, so its
// SA-owned fields are locked against local edits (TAV-3233 issue 3).
export const ORIGIN_TAG_API = 'api';

// TAV-3233 issue 4: the value-bearing columns clip inside their <input> with no ellipsis once the
// browser compresses the column below its content width (table-layout: auto sizes columns largely
// by header length). Wraps the default editor with a `title` carrying the full value so a value
// that still ends up shortened is recognisable as such on hover.
function withFullValueTitle(editor: EditorRenderer): EditorRenderer {
  // eslint-disable-next-line react/display-name -- matches the columnEditors.tsx factory pattern this wraps
  return (value, row, onChange, context, isDisabled) =>
    React.createElement(
      'span',
      { title: value !== null && value !== undefined && value !== '' ? String(value) : undefined, style: { display: 'block' } },
      editor(value, row, onChange, context, isDisabled),
    );
}

// The Price Plans currency field (trimmed, borderless `$` prefix) without the fixed table layout
// that `minimalView` would bring (see table-view/_minimal.scss).
const CONDENSED_CURRENCY = 'condensed-currency';
// Total and Used: the Price Plans rate width (105px, a five-figure amount beside the trimmed `$`)
// plus 5px so a six-figure total still fits. It was 130px while the field carried Bootstrap's full
// addon padding.
const CURRENCY_COLUMN_STYLE = { minWidth: '110px' };

// Shared by Start Date and End Date. See the start_date column for why both properties are set.
const DATE_COLUMN_STYLE = { width: '150px', minWidth: '150px' };

interface ColumnsFactoryOptions {
  saConnected: boolean;
  isDirty: boolean;
  isProcessing: boolean;
  canElectToReturn: boolean;
  onRollover: (row: Row) => void;
  onElectToReturn: (row: Row) => void;
}

// TAV-2601 / TAV-2602 — converted to a factory (matching the sibling listAll.ts) so the new
// actions column can carry connectivity/dirty/auth-tag state and the rollover / elect-to-return
// callbacks. Deliberately NOT adding a `status` display column here — see Decision E2:
// filterBudgetRowFields derives its update-payload allowlist from column ids, so a `status`
// column would let a stale local 'OPEN' be PUT back after a successful rollover and revert the
// closure in the database. Status is rendered as a badge inside SahBudgetActions instead.
const columnsFactory = ({
  saConnected,
  isDirty,
  isProcessing,
  canElectToReturn,
  onRollover,
  onElectToReturn,
}: ColumnsFactoryOptions): Column[] => {
  const columns: Column[] = [
    {
      id: 'sah_budget_id',
      header: 'Budget ID',
      type: 'text',
      // TAV-3332: no width, so the column sizes to the longest ID under table-layout: auto rather
      // than a fixed 170px; the modal's wrapper scrolls when the columns do not fit.
      isEditable: (row) => row.origin_tag !== ORIGIN_TAG_API,
      validation: Yup.string().required('Budget ID is required'),
    },
    {
      id: 'start_date',
      header: 'Start Date',
      type: 'date',
      // TAV-3089 sets the width so the full year is visible; the matching minWidth keeps the
      // browser from compressing it back below that under table-layout: auto (TAV-3233 issue 4).
      style: DATE_COLUMN_STYLE,
      isEditable: (row) => row.origin_tag !== ORIGIN_TAG_API,
      validation: Yup.string().required('Start date is required'),
    },
    {
      id: 'end_date',
      header: 'End Date',
      type: 'date',
      style: DATE_COLUMN_STYLE,
      isEditable: (row) => row.origin_tag !== ORIGIN_TAG_API,
      validation: Yup.string()
        .required('End date is required')
        .test('end-after-start', 'End date must be after start date', function (value) {
          const { start_date } = this.parent;
          if (!value || !start_date) return true;
          return new Date(value) >= new Date(start_date);
        }),
    },
    {
      id: 'total_amount',
      header: 'Total',
      type: 'currency',
      style: CURRENCY_COLUMN_STYLE,
      classNames: { header: 'header-align-right', cell: 'cell-align-right', column: CONDENSED_CURRENCY },
      isEditable: (row) => row.origin_tag !== ORIGIN_TAG_API,
    },
    {
      id: 'used_amount',
      header: 'Used',
      type: 'currency',
      style: CURRENCY_COLUMN_STYLE,
      classNames: { header: 'header-align-right', cell: 'cell-align-right text-danger', column: CONDENSED_CURRENCY },
      isEditable: (row) => row.origin_tag !== ORIGIN_TAG_API,
    },
    {
      id: 'available_amount',
      header: 'Available',
      type: 'currency',
      isEditable: false,
      classNames: { header: 'header-align-right', cell: 'cell-align-right text-muted' },
      // TAV-2977: available_amount is uploaded/stored per row, not derived from total/used. The
      // sign convention of used_amount varies by upload source, so re-deriving from it here
      // previously inflated Available above Total. Trust the stored value, clamped to Total
      // defensively for the rare rows where the stored figure is itself inconsistent. A row that
      // has never had an authoritative value set (available_amount === undefined, e.g. a row just
      // added via Add Row, before any save) has no stored figure to trust, so fall back to the
      // Total - Used estimate in that case only.
      deriveValue: (row) => {
        if (row.available_amount === undefined) {
          return (row.total_amount ?? 0) - (row.used_amount ?? 0);
        }
        return Math.min(row.total_amount ?? 0, row.available_amount ?? 0);
      },
    },
    {
      id: 'prior_spent_amount',
      header: 'Prior-Spent Amount',
      caption:
        'Spend a client had with a previous provider before transferring to this budget. Deducted once from their opening balance.',
      type: 'currency',
      classNames: { header: 'header-align-right', cell: 'cell-align-right', column: CONDENSED_CURRENCY },
      isEditable: (row) => !row.prior_spent_included_in_statements,
      validation: Yup.number()
        .nullable()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .min(0, 'Prior-spent amount cannot be negative')
        .test('requires-prior-spent-date', 'Prior-spent date is required when an amount is set', function (value) {
          const { prior_spent_date } = this.parent;
          if (value === undefined || value === null) return true;
          return Boolean(prior_spent_date);
        }),
    },
    {
      id: 'prior_spent_date',
      header: 'Prior-Spent Date',
      caption: 'The date from which the Prior-Spent Amount should be applied to this budget’s opening balance.',
      type: 'date',
      isEditable: (row) => !row.prior_spent_included_in_statements,
      validation: Yup.string()
        .nullable()
        .test('requires-prior-spent-amount', 'Prior-spent amount is required when a date is set', function (value) {
          const { prior_spent_amount } = this.parent;
          if (!value) return true;
          return prior_spent_amount !== undefined && prior_spent_amount !== null;
        }),
    },
    {
      id: 'prior_spent_included_in_statements',
      header: 'Prior-Spent Applied',
      caption: 'Set automatically once a statement has been generated using the Prior-Spent Amount. Cannot be edited.',
      type: 'checkbox',
      isEditable: false,
      classNames: { header: 'header-align-center', cell: 'cell-align-center' },
    },
    {
      id: 'is_archived',
      header: 'Is Archived',
      type: 'checkbox',
      classNames: { header: 'header-align-center', cell: 'cell-align-center' },
    },
    // TAV-3332: only for a company connected to Services Australia. Without a connection neither
    // action can be offered and SA status cannot be refreshed, so the column is left out rather
    // than shown with stale badges or empty cells.
    ...(saConnected
      ? [
          {
            id: 'sah_actions',
            header: 'SA Status',
            type: 'custom' as const,
            // TableView centres a header through th.cell-align-center (_thead.scss);
            // header-align-center belongs to the older table and is a no-op here.
            classNames: { header: 'cell-align-center', cell: 'cell-align-center' },
            component: SahBudgetActions,
            componentProps: (row: Row) => ({
              row,
              saConnected,
              isDirty,
              isProcessing,
              canElectToReturn,
              onRollover,
              onElectToReturn,
            }),
          },
        ]
      : []),
  ];

  // Applied after construction so each editor factory can be handed the finished column (needed
  // for its cell alignment classNames, matching how TableView itself resolves the default editor).
  const budgetIdColumn = columns.find((column) => column.id === 'sah_budget_id');
  if (budgetIdColumn) budgetIdColumn.renderEditor = withFullValueTitle(textEditor('sah_budget_id', budgetIdColumn));

  const startDateColumn = columns.find((column) => column.id === 'start_date');
  if (startDateColumn) {
    startDateColumn.renderEditor = withFullValueTitle(dateEditor('start_date', undefined, undefined, startDateColumn));
  }

  const endDateColumn = columns.find((column) => column.id === 'end_date');
  if (endDateColumn) {
    endDateColumn.renderEditor = withFullValueTitle(dateEditor('end_date', undefined, undefined, endDateColumn));
  }

  const totalAmountColumn = columns.find((column) => column.id === 'total_amount');
  if (totalAmountColumn) {
    totalAmountColumn.renderEditor = withFullValueTitle(currencyEditor('total_amount', false, totalAmountColumn));
  }

  const usedAmountColumn = columns.find((column) => column.id === 'used_amount');
  if (usedAmountColumn) {
    usedAmountColumn.renderEditor = withFullValueTitle(currencyEditor('used_amount', false, usedAmountColumn));
  }

  return columns;
};

export default columnsFactory;
